import { Theme, useMediaQuery, IconButton } from "@material-ui/core";
import { DeleteForever } from "@material-ui/icons";
import * as React from "react";
import { FC, Fragment } from "react";
import {
  Datagrid,
  Filter,
  FilterProps,
  FunctionField,
  ListContextProvider,
  ListProps,
  SelectInput,
  TextField,
  TextInput,
  useListContext,
  useNotify,
  useRefresh,
} from "react-admin";
import { useAppDataProvider } from "../../dataprovider";
import { useAuth } from "@airborne/react-admin";
import { EnumField } from "../../../src/Components/EnumField";
import CreatedByField from "../../../src/Components/ReferenceFields/CreatedBy/CreatedByField";
import { RequestExcusableShippingType } from "../../../src/Enums/RequestExcusableShippingType";
import { RequestExcusableStatus } from "../../../src/Enums/RequestExcusableStatus";
import { ExcelExport } from "../../../src/Helper/ExportExcel";
import { ExcusableExcelHeader, ExcusableExcelName } from "../../../src/Pages/Excusable/Constant/Excel";
import { CustomDateField } from "../../Components/CustomDateField";
import { CustomDatetime } from "../../Components/CustomDatetime";
import { DefaultCreatedDateSort, DefaultPerPageSize } from "../../Constants/Constant";
import { excusableStatus, shippingTypes } from "./Constant/Source";
import ExcusableDetailButton from "./ExcusableDetailButton";
import PoolList from "../../Components/PoolListComponent/PoolListComponent";
import CopyableTableWrapper from "../../../src/Components/CopyableTableWrapper";

const ExcusableRBHFilter: FC<Omit<FilterProps, "children">> = props => (
  <Filter {...props}>
    <TextInput label="Ref No" source="refNo__contains" alwaysOn />
    <TextInput label="Customer Code" source="customerCode__contains" alwaysOn />
    <TextInput label="Pn" source="pn__contains" alwaysOn />
    <TextInput label="Sn" source="sn__contains" alwaysOn />
    <SelectInput
      source="shippingType__eq"
      alwaysOn
      label="SHIPPING TYPE"
      choices={shippingTypes}
      optionText="label"
      optionValue="id"
    />
    <SelectInput
      source="excusableStatus__eq"
      alwaysOn
      label="Status"
      choices={excusableStatus}
      optionText="label"
      optionValue="id"
    />
    <CustomDatetime source="createdDate__gte" label="Created Date (>=)" alwaysOn />
    <CustomDatetime source="createdDate__lte" label="Created Date (<=)" alwaysOn />
    <CustomDatetime source="requestedDeliveryDate__gte" label="Requested Delivery Date (>=)" alwaysOn />
    <CustomDatetime source="requestedDeliveryDate__lte" label="Requested Delivery Date (<=)" alwaysOn />
  </Filter>
);

const ExportToExcel = data => {
  ExcelExport(
    ExcusableExcelHeader,
    data.map(v => [
      v.refNo,
      v.customerCode, // Burası düzenlenecek.!
      v.requestCreateDate,
      v.pn,
      v.sn,
      v.flightDate,
      v.estimatedDeliveryDate,
      RequestExcusableShippingType[v.shippingType],
      v.createdBy,
      v.createdDate,
      v.requestedDeliveryDate,
      RequestExcusableStatus[v.excusableStatus],
    ]),
    ExcusableExcelName
  );
};

const RBHDatagrid: FC = props => {
  const listContext = useListContext();
  const isXSmall = useMediaQuery<Theme>(theme => theme.breakpoints.down("xs"));
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useAppDataProvider();
  const { user } = useAuth();

  const handleDelete = (record: any) => {
    dataProvider
      .deleteRequestExcusable(record?.id)
      .then(() => {
        notify('Record deleted', 'success', false, true);
        refresh();
      })
      .catch(error => {
        notify(`Error: ${error?.response?.data?.message ?? error}`, 'error', false, true);
      });
  };

  return (
    <Fragment>
      {isXSmall ? (
        <ListContextProvider value={{ ...listContext }}>
          <></>
        </ListContextProvider>
      ) : (
        <ListContextProvider
          value={{
            ...listContext,
          }}
        >
          <CopyableTableWrapper>
            <Datagrid {...props} hasBulkActions={false} optimized>
              <ExcusableDetailButton label="Actions" resource="requestorderexcusables" />
              <FunctionField
                label="Delete"
                render={(record: any) =>
                  record?.excusableStatus !== RequestExcusableStatus.Rejected &&
                  record?.createdBy === user?.id ? (
                    <IconButton
                      size="small"
                      onClick={e => {
                        e.stopPropagation();
                        handleDelete(record);
                      }}
                      style={{ color: '#C90C0F' }}
                    >
                      <DeleteForever fontSize="small" />
                    </IconButton>
                  ) : null
                }
              />
              <TextField source="refNo" label={"Ref No"} />
              <TextField source="customerCode" label={"Customer Name"} />
              <CustomDateField source="requestCreateDate" label={"Request Date"} showTime={false} locales="en-GB" />
              <TextField source="pn" label={"PN"} />
              <TextField source="sn" label={"SN"} />
              <EnumField source={"shippingType"} label={"Shipping Type"} enumName={"RequestExcusableShippingType"} />
              <CreatedByField label="Created by" link={false} />
              <CustomDateField source="createdDate" locales="en-GB" />
              <CustomDateField source="requestedDeliveryDate" locales="en-GB" />
              <EnumField source={"excusableStatus"} label={"Status"} enumName={"RequestExcusableStatus"} />
            </Datagrid>
          </CopyableTableWrapper>
        </ListContextProvider>
      )}
    </Fragment>
  );
};

const ExcusableListRBH: FC<ListProps> = props => (
  <PoolList
    {...props}
    title="RBH Excusable List"
    sort={DefaultCreatedDateSort}
    perPage={DefaultPerPageSize}
    basePath="/requestorderexcusables"
    resource="requestordersexcusables/rbh"
    exporter={ExportToExcel}
    hasCreate={true}
    filters={<ExcusableRBHFilter />}
  >
    <RBHDatagrid />
  </PoolList>
);

export default ExcusableListRBH;
